import { cn } from "@/lib/utils";
import apiRoutes from "@/src/apiRoutes";
import { textVariants } from "@/src/components/Text/Text";
import api from "@/src/utils";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ReferralOverview, ReferredData } from "../types";
import moment from "moment";
import { isEmpty } from "lodash";
import { toast } from "sonner";
import referandearn from "../../../../assets/image.png";
import { Tabs } from "@mantine/core";
import { Coins, Link2, Share2 } from "lucide-react";
import HowItWorks from "./HowItWorks";
import Copy from "./Copy";
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  LinkedinShareButton,
  LinkedinIcon,
  TwitterShareButton,
  WhatsappShareButton,
  TwitterIcon,
  WhatsappIcon,
} from "react-share";
import LinkCopy from "./LinkCopy";
import { useTranslation } from "react-i18next";

const Refer = () => {
  const { t } = useTranslation();
  const trans = (key: string) => t(`app.referAndEarn.refer.${key}`);
  const getReferralsOverview = async () => {
    const res = await api.post(apiRoutes.referralData, {
      type: "overview",
    });
    return res.data;
  };

  const { data } = useQuery<ReferralOverview>({
    queryKey: ["referral_overview"],
    queryFn: getReferralsOverview,
  });

  const getReferralsHistory = async () => {
    const res = await api.post(apiRoutes.referralData, {
      type: "history",
    });
    return res.data;
  };

  const { data: historyData } = useQuery<ReferredData>({
    queryKey: ["referral_history"],
    queryFn: getReferralsHistory,
  });

  const withdraw = async () => {
    const res = await api.post(apiRoutes.rewards, {
      type: "withdraw",
    });
    return res.data;
  };

  const { mutate } = useMutation({
    mutationFn: withdraw,
    onSuccess: (data) => {
      if (data?.success) {
        toast.success(data?.info);
      } else {
        toast.warning(data?.info);
      }
    },
  });

  return (
    <div className="py-6">
      <div className="bg-blue-100 w-full dark:bg-[#212741] px-3 py-4 md:p-4 rounded-lg flex md:grid md:grid-cols-4 justify-between items-center">
        <div className="md:col-span-3 w-2/3 md:max-w-lg">
          <h1
            className={cn(
              textVariants({ size: "xxxxl", font: "bold" }),
              "max-md:text-base"
            )}
          >
            {trans("header")}
          </h1>
          <p
            className={cn(
              textVariants({ size: "md", variant: "desc" }),
              "max-md:text-xs"
            )}
          >
            {trans("description")}
          </p>
        </div>
        <div className="md:col-span-1 flex justify-center items-center">
          <img
            src={referandearn}
            alt="copy"
            className="cursor-pointer w-24 md:w-56"
          />
        </div>
      </div>
      <Tabs defaultValue="share" className="py-10">
        <Tabs.List className="grid grid-cols-2">
          <Tabs.Tab
            value="share"
            className={cn(
              textVariants({ size: "xl", font: "semibold" }),
              "dark:hover:text-white dark:hover:bg-[#212741] max-md:text-sm"
            )}
          >
            {trans("tabs.shareInvite")}
          </Tabs.Tab>
          <Tabs.Tab
            value="rewards"
            className={cn(
              textVariants({ size: "xl", font: "semibold" }),
              "dark:hover:text-white dark:hover:bg-[#212741] max-md:text-sm"
            )}
          >
            {trans("tabs.referralStatus")}
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="share">
          <div className="bg-blue-50 dark:bg-[#212741] flex rounded-md rounded-t-none flex-col py-8 gap-4 md:gap-8 px-4 md:p-8">
            <div className="flex justify-between items-center md:gap-24 gap-12">
              <div className="flex md:col-span-5 flex-col md:gap-2">
                <h3
                  className={cn(
                    textVariants({ size: "xxxxl", font: "bold" }),
                    "max-md:text-base"
                  )}
                >
                  {trans("shareInvite.title")}
                </h3>
                <p
                  className={cn(
                    textVariants({ size: "md", variant: "desc" }),
                    "max-md:text-xs"
                  )}
                >
                  {trans("shareInvite.desc")}
                </p>
              </div>
              <div className="w-fit flex flex-col items-center justify-center px-2 md:px-4 py-2 md:p-4 gap-2 border border-white dark:md:border-[#2F60E0]/20 rounded-md">
                <Link2
                  className={cn(
                    textVariants({ font: "normal" }),
                    "text-center max-md:text-sm w-4 h-4"
                  )}
                />
                <LinkCopy title={data?.info.referral_link as string} />
              </div>
            </div>
            <div className="flex w-full md:grid md:grid-cols-3 justify-center items-center text-white">
              <span
                className={cn(
                  textVariants({ size: "lg", font: "semibold" }),
                  "line-clamp-1 w-full bg-blue-100 dark:bg-[#1A1F32] md:col-span-2 rounded-md rounded-r-none px-4 py-4 max-md:text-sm"
                )}
              >
                {trans("shareInvite.copyReferralCode")}
              </span>
              <Copy title={data?.info.referral_code as string} />
              {/* <span className='bg-blue-900 rounded-r-md px-4 py-4 flex items-center justify-center gap-1'>
                scarsheretostay <CopyIcon2 className='w-4 h-4 ms-auto' />
              </span> */}
            </div>
            <div className="w-full flex justify-between items-center lg:grid lg:grid-cols-7 md:gap-24">
              <span className="md:cols-span-1 md:border md:border-white dark:md:border-[#2F60E0]/20 dark:text-white py-4 px-4 rounded-md max-md:rounded-r-none flex items-center justify-center">
                <Share2 />
              </span>

              <span className="w-full rounded-md max-md:rounded-l-none text-white py-4 md:col-span-6 flex gap-4 justify-start items-center">
                <div className="flex gap-2 md:gap-4">
                  <EmailShareButton
                    url={`https://app.mybitstore.com/auth/join/${data?.info.referral_code}`}
                    subject="Download Mybitstore"
                    body={`Join me on this amazing app! Use my referral code: ${data?.info.referral_code}`}
                  >
                    <EmailIcon size={48} round className="p-1" />
                  </EmailShareButton>
                  <WhatsappShareButton
                    title="Invite on Mybitstore"
                    url={`https://app.mybitstore.com/auth/join/${data?.info.referral_code}`}
                  >
                    <WhatsappIcon size={48} round className="p-1" />
                  </WhatsappShareButton>
                  <FacebookShareButton
                    url={`https://app.mybitstore.com/auth/join/${data?.info.referral_code}`}
                    title={`Join me on this amazing app! Use my referral code: ${data?.info.referral_code}`}
                    hashtag="#Mybitstore"
                  >
                    <FacebookIcon size={48} round className="p-1" />
                  </FacebookShareButton>
                  <TwitterShareButton
                    url={`https://app.mybitstore.com/auth/join/${data?.info.referral_code}`}
                    title={`Join me on this amazing app! Use my referral code: ${data?.info.referral_code}`}
                    hashtags={[
                      "Mybitstore",
                      "ReferAndEarn",
                      "Crypto",
                      "Ghana",
                      "Africa",
                    ]}
                  >
                    <TwitterIcon size={48} round className="p-1" />
                  </TwitterShareButton>
                  <LinkedinShareButton
                    url={`https://app.mybitstore.com/auth/join/${data?.info.referral_code}`}
                    summary={`Join me on this amazing app! Use my referral code: ${data?.info.referral_code}`}
                  >
                    <LinkedinIcon size={48} round className="p-1" />
                  </LinkedinShareButton>
                </div>
              </span>
            </div>
            {/* <Modal
              opened={opened}
              onClose={close}
              title='Share Mybitstore to friends and start earning'
              size='lg'
              centered
            >
              
            </Modal> */}
          </div>
          <HowItWorks />
        </Tabs.Panel>

        <Tabs.Panel value="rewards">
          <div className="bg-blue-50 w-full dark:bg-[#212741] px-3 py-4 md:p-4 rounded-lg rounded-t-none">
            <div className="flex justify-between items-center">
              <div className="flex flex-col gap-1">
                <p
                  className={cn(
                    textVariants({ font: "normal" }),
                    "max-md:text-base"
                  )}
                >
                  $0.00
                </p>
                <p
                  className={cn(
                    textVariants({ font: "normal" }),
                    "text-center max-md:text-xs whitespace-nowrap"
                  )}
                >
                  {trans("referralStatus.pendingCashback")}
                </p>
              </div>
              <div className="flex flex-col gap-1">
                <span className="flex gap-1 bg-[#2F60E0]/30 dark:bg-[#2F60E0]/20 rounded-md px-4 py-2 text-white dark:text-gray-900]">
                  <p
                    className={cn(
                      textVariants({ font: "normal" }),
                      "text-center max-md:text-xs"
                    )}
                  >
                    ${data?.info.total_earnings}
                  </p>
                  <p
                    className={cn(
                      textVariants({ font: "normal" }),
                      "text-right max-md:text-xs"
                    )}
                  >
                    {trans("referralStatus.totalEarnings")}
                  </p>
                </span>
                <span
                  className={cn(
                    textVariants({ font: "normal" }),
                    "text-right max-md:text-xs whitespace-nowrap"
                  )}
                >
                  {data?.info.number_of_referrals}{" "}
                  {trans("referralStatus.numberOfReferrals")}
                </span>
              </div>
            </div>
            <hr className="my-5 border-1.5 border-gray-200 dark:border-grey-400" />
            <div className="flex justify-between gap-10 items-center">
              <span
                onClick={() => mutate()}
                className="flex gap-1 bg-[#2F60E0]/30 dark:bg-[#2F60E0]/20 rounded-md px-4 py-2 text-white dark:text-gray-900] cursor-pointer"
              >
                <Coins
                  className={cn(
                    textVariants({ font: "normal" }),
                    "text-center max-md:text-xs w-5 h-5 md:w-8 md:h-8"
                  )}
                />
                <p
                  className={cn(
                    textVariants({ font: "normal" }),
                    "text-right max-md:text-xs"
                  )}
                >
                  {trans("referralStatus.withdrawEarnings")}
                </p>
              </span>
              <p
                className={cn(
                  textVariants({ font: "normal" }),
                  "text-right max-md:text-xs"
                )}
              >
                {trans("referralStatus.boostEarnings")}
              </p>
            </div>
          </div>
          <>
            <h3
              className={cn(
                textVariants({ size: "xxl", font: "bold" }),
                "max-md:text-base my-6 md:my-12"
              )}
            >
              {trans("history.header")}
            </h3>

            {!isEmpty(historyData?.info) && (
              <div className="grid">
                <span
                  className={textVariants({ size: "xl", variant: "header" })}
                >
                  {trans("history.subHeader")}
                </span>
                <div className="grid grid-cols-4 bg-white dark:bg-grey-700 mt-3 p-2">
                  <span className={textVariants({ variant: "header" })}>
                    {trans("history.table.headers.coin")}
                  </span>
                  <span className={textVariants({ variant: "header" })}>
                    {trans("history.table.headers.date")}
                  </span>
                  <span className={textVariants({ variant: "header" })}>
                    {trans("history.table.headers.cryptoAmount")}
                  </span>
                  <span className={textVariants({ variant: "header" })}>
                    {trans("history.table.headers.amount")}
                  </span>
                </div>
                {historyData?.info.map((item, idx) => (
                  <div
                    key={idx}
                    className="grid grid-cols-4 border border-gray-200 dark:border-grey-500 py-3 px-2"
                  >
                    <div className="flex items-center gap-1">
                      <img
                        src={item?.coin_image}
                        className="h-5 w-5"
                        alt={item?.coin_ticker}
                      />
                      <span
                        className={textVariants({
                          variant: "desc",
                          case: "uppercase",
                        })}
                      >
                        {item?.coin_ticker}
                      </span>
                    </div>
                    <span
                      className={textVariants({ size: "sm", variant: "desc" })}
                    >
                      {moment(item?.created_at).format("lll")}
                    </span>
                    <span
                      className={textVariants({ size: "sm", variant: "desc" })}
                    >
                      {item?.amount_crypto}
                    </span>
                    <span className={textVariants({ size: "sm" })}>
                      {item?.amount}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        </Tabs.Panel>
      </Tabs>
    </div>
  );
};

export default Refer;
